import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { PROFIL_STATUS, QUALIFIKATIONEN, BUNDESLAENDER } from '../../lib/constants'
import type { Pflegekraft } from '../../lib/types'
import './Dashboard.css'

interface DashboardStats {
  totalProfile: number
  neueProfile: number
  matchbareProfile: number
  inVermittlung: number
  platziert: number
  offeneStellen: number
  totalArbeitgeber: number
  offeneMatches: number
  topMatches: number
  guteMatches: number
  totalEvents: number
}

interface MatchSummary {
  pflegekraft_name: string
  einrichtung_name: string
  score: number
  kategorie: string
  pflegekraft_id: string
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    totalProfile: 0, neueProfile: 0, matchbareProfile: 0, inVermittlung: 0,
    platziert: 0, offeneStellen: 0, totalArbeitgeber: 0, offeneMatches: 0,
    topMatches: 0, guteMatches: 0, totalEvents: 0,
  })
  const [recentProfiles, setRecentProfiles] = useState<Pflegekraft[]>([])
  const [topMatchList, setTopMatchList] = useState<MatchSummary[]>([])
  const [statusDist, setStatusDist] = useState<{ status: string; count: number }[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadDashboardData() }, [])

  async function loadDashboardData() {
    try {
      const [
        { count: totalProfile },
        { count: neueProfile },
        { count: matchbareProfile },
        { count: inVermittlung },
        { count: platziert },
        { count: offeneStellen },
        { count: totalArbeitgeber },
        { count: offeneMatches },
        { count: topMatches },
        { count: guteMatches },
        { count: totalEvents },
        { data: recent },
        { data: topMatchData },
        { data: allPk },
      ] = await Promise.all([
        supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }),
        supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }).eq('status', 'neu'),
        supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }).eq('status', 'matchbar'),
        supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }).eq('status', 'in_vermittlung'),
        supabase.from('pflegekraefte').select('*', { count: 'exact', head: true }).eq('status', 'platziert'),
        supabase.from('stellen').select('*', { count: 'exact', head: true }).eq('status', 'aktiv'),
        supabase.from('arbeitgeber').select('*', { count: 'exact', head: true }),
        supabase.from('matches').select('*', { count: 'exact', head: true }).is('manuell_bestaetigt', null).eq('knockout', false).not('arbeitgeber_id', 'is', null),
        supabase.from('matches').select('*', { count: 'exact', head: true }).gte('score', 80).not('arbeitgeber_id', 'is', null),
        supabase.from('matches').select('*', { count: 'exact', head: true }).gte('score', 60).lt('score', 80).not('arbeitgeber_id', 'is', null),
        supabase.from('pipeline_events').select('*', { count: 'exact', head: true }),
        supabase.from('pflegekraefte').select('*').order('created_at', { ascending: false }).limit(8),
        supabase.from('matches').select(`
          score, kategorie,
          pflegekraefte!inner(id, vorname, nachname),
          arbeitgeber!inner(name)
        `).not('arbeitgeber_id', 'is', null).order('score', { ascending: false }).limit(5),
        supabase.from('pflegekraefte').select('status'),
      ])

      setStats({
        totalProfile: totalProfile ?? 0, neueProfile: neueProfile ?? 0,
        matchbareProfile: matchbareProfile ?? 0, inVermittlung: inVermittlung ?? 0,
        platziert: platziert ?? 0, offeneStellen: offeneStellen ?? 0,
        totalArbeitgeber: totalArbeitgeber ?? 0, offeneMatches: offeneMatches ?? 0,
        topMatches: topMatches ?? 0, guteMatches: guteMatches ?? 0,
        totalEvents: totalEvents ?? 0,
      })
      setRecentProfiles((recent as Pflegekraft[]) || [])

      // Top matches
      if (topMatchData) {
        setTopMatchList(topMatchData.map((m: any) => ({
          pflegekraft_name: `${m.pflegekraefte.vorname} ${m.pflegekraefte.nachname}`,
          einrichtung_name: m.arbeitgeber.name,
          score: m.score,
          kategorie: m.kategorie,
          pflegekraft_id: m.pflegekraefte.id,
        })))
      }

      // Status distribution
      if (allPk) {
        const dist = PROFIL_STATUS.map(s => ({
          status: s.value,
          count: allPk.filter((p: any) => p.status === s.value).length,
        })).filter(d => d.count > 0)
        setStatusDist(dist)
      }
    } catch (err) {
      console.error('Dashboard load error:', err)
    } finally {
      setLoading(false)
    }
  }

  const statusColor = (status: string) =>
    PROFIL_STATUS.find((s) => s.value === status)?.color || '#94A3B8'
  const statusLabel = (status: string) =>
    PROFIL_STATUS.find((s) => s.value === status)?.label || status
  const qualLabel = (val: string) =>
    QUALIFIKATIONEN.find(q => q.value === val)?.label || val || '–'
  const blLabel = (val: string) =>
    BUNDESLAENDER.find(b => b.value === val)?.label || val || '–'

  const totalStatusCount = statusDist.reduce((s, d) => s + d.count, 0) || 1

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="dashboard animate-fade-in-up">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Übersicht aller Vermittlungsaktivitäten</p>
        </div>
        <div className="dashboard-actions">
          <button className="btn btn--primary" onClick={() => navigate('/admin/matching')}>
            🎯 Matching starten
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card stat-card--primary" onClick={() => navigate('/admin/pflegekraefte')}>
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalProfile}</span>
            <span className="stat-label">Profile gesamt</span>
          </div>
        </div>
        <div className="stat-card stat-card--blue" onClick={() => navigate('/admin/pipeline')}>
          <div className="stat-icon">🆕</div>
          <div className="stat-content">
            <span className="stat-value">{stats.neueProfile}</span>
            <span className="stat-label">Neue Profile</span>
          </div>
        </div>
        <div className="stat-card stat-card--amber" onClick={() => navigate('/admin/pipeline')}>
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <span className="stat-value">{stats.inVermittlung}</span>
            <span className="stat-label">In Vermittlung</span>
          </div>
        </div>
        <div className="stat-card stat-card--gold" onClick={() => navigate('/admin/pipeline')}>
          <div className="stat-icon">🎉</div>
          <div className="stat-content">
            <span className="stat-value">{stats.platziert}</span>
            <span className="stat-label">Platziert</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => navigate('/admin/stellen')}>
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <span className="stat-value">{stats.offeneStellen}</span>
            <span className="stat-label">Offene Stellen</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => navigate('/admin/arbeitgeber')}>
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalArbeitgeber}</span>
            <span className="stat-label">Arbeitgeber</span>
          </div>
        </div>
        <div className="stat-card stat-card--purple" onClick={() => navigate('/admin/matching')}>
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <span className="stat-value">{stats.offeneMatches}</span>
            <span className="stat-label">Offene Matches</span>
          </div>
        </div>
        <div className="stat-card stat-card--green" onClick={() => navigate('/admin/matching')}>
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <span className="stat-value">{stats.topMatches}</span>
            <span className="stat-label">Top Matches</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Status Distribution */}
        <div className="dashboard-section">
          <h2>Pipeline-Verteilung</h2>
          {statusDist.length > 0 ? (
            <div className="status-distribution">
              {/* Visual bar */}
              <div className="status-bar">
                {statusDist.map(d => (
                  <div
                    key={d.status}
                    className="status-bar-segment"
                    style={{
                      width: `${(d.count / totalStatusCount) * 100}%`,
                      background: statusColor(d.status),
                    }}
                    title={`${statusLabel(d.status)}: ${d.count}`}
                  />
                ))}
              </div>
              {/* Legend */}
              <div className="status-legend">
                {statusDist.map(d => (
                  <div key={d.status} className="legend-item">
                    <span className="legend-dot" style={{ background: statusColor(d.status) }} />
                    <span className="legend-label">{statusLabel(d.status)}</span>
                    <span className="legend-count">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state-sm">
              <p>Noch keine Profile im System.</p>
            </div>
          )}
        </div>

        {/* Top Matches */}
        <div className="dashboard-section">
          <h2>Top Matches</h2>
          {topMatchList.length > 0 ? (
            <div className="top-matches-list">
              {topMatchList.map((m, i) => (
                <div key={i} className="top-match-item" onClick={() => navigate(`/admin/pflegekraefte/${m.pflegekraft_id}`)}>
                  <div className="top-match-score" style={{
                    color: m.score >= 80 ? '#10B981' : m.score >= 60 ? '#3B82F6' : '#F59E0B'
                  }}>
                    {m.score}%
                  </div>
                  <div className="top-match-info">
                    <span className="top-match-name">👤 {m.pflegekraft_name}</span>
                    <span className="top-match-stelle">↔ 🏢 {m.einrichtung_name}</span>
                  </div>
                  <span className="top-match-badge" style={{
                    background: m.score >= 80 ? '#10B981' : m.score >= 60 ? '#3B82F6' : '#F59E0B'
                  }}>
                    {m.score >= 80 ? '🏆' : m.score >= 60 ? '👍' : '🤔'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state-sm">
              <p>Noch keine Matches. Starten Sie das Matching.</p>
              <button className="btn btn--secondary btn--sm" onClick={() => navigate('/admin/matching')}>
                Zum Matching →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Recent Profiles Table */}
      <div className="dashboard-section dashboard-section--full">
        <div className="section-header-row">
          <h2>Neueste Profile</h2>
          <button className="btn btn--secondary btn--sm" onClick={() => navigate('/admin/pflegekraefte')}>
            Alle anzeigen →
          </button>
        </div>
        {recentProfiles.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <p>Noch keine Profile vorhanden.</p>
            <p className="empty-hint">Profile werden über den Funnel erstellt.</p>
          </div>
        ) : (
          <div className="recent-table-wrapper">
            <table className="recent-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Qualifikation</th>
                  <th>Bundesland</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                </tr>
              </thead>
              <tbody>
                {recentProfiles.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/admin/pflegekraefte/${p.id}`)}>
                    <td className="cell-name">
                      <span>{p.vorname} {p.nachname}</span>
                      <span className="cell-sub">{p.email}</span>
                    </td>
                    <td>{qualLabel(p.qualifikation || '')}</td>
                    <td>{blLabel(p.bundesland || '')}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ '--badge-color': statusColor(p.status) } as React.CSSProperties}
                      >
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td className="cell-date">
                      {new Date(p.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section dashboard-section--full">
        <h2>Schnellzugriff</h2>
        <div className="quick-actions">
          <div className="quick-action" onClick={() => navigate('/admin/matching')}>
            <span className="quick-icon">🎯</span>
            <span className="quick-label">Matching starten</span>
            <span className="quick-desc">Kandidaten ↔ Stellen abgleichen</span>
          </div>
          <div className="quick-action" onClick={() => navigate('/admin/pipeline')}>
            <span className="quick-icon">📈</span>
            <span className="quick-label">Pipeline Board</span>
            <span className="quick-desc">Vermittlungsstatus verwalten</span>
          </div>
          <div className="quick-action" onClick={() => navigate('/admin/stellen')}>
            <span className="quick-icon">📋</span>
            <span className="quick-label">Stellen verwalten</span>
            <span className="quick-desc">Offene Positionen bearbeiten</span>
          </div>
          <div className="quick-action" onClick={() => navigate('/admin/arbeitgeber')}>
            <span className="quick-icon">🏢</span>
            <span className="quick-label">Arbeitgeber</span>
            <span className="quick-desc">Partner-Einrichtungen verwalten</span>
          </div>
        </div>
      </div>
    </div>
  )
}
